// Copyright (c) 2026. LodgIQ Inc, All rights reserved
//
// AL-59 — revenue-management vocabulary repair for dictated questions.
//
// Browser speech engines have never heard of RevPAR. Left raw, dictation
// produces "what's my rev par for august first" — which still works, but reads
// like a typo to the user and muddies the transcript we log. This is a pure
// string pass applied to the FINAL transcript only (never to interim text, so
// words don't visibly rewrite themselves mid-sentence).
//
// Rules are deliberately conservative: fix house vocabulary and obvious dictation
// artefacts, never guess at numbers or dates the user didn't say.

const TERMS = [
  // [pattern, replacement] — \b keeps us off substrings ("badr", "otbx")
  [/\brev\s*[- ]?\s*par\b/gi, "RevPAR"],
  [/\br\s*ev\s*p\s*a\s*r\b/gi, "RevPAR"],
  [/\bg\s*o\s*p\s*par\b/gi, "GOPPAR"],
  [/\btrev\s*par\b/gi, "TRevPAR"],
  [/\ba\.?\s?d\.?\s?r\.?\b/gi, "ADR"],
  [/\bo\.?\s?t\.?\s?b\.?\b/gi, "OTB"],
  [/\bon\s+the\s+books\b/gi, "on the books"],
  [/\bo\.?\s?o\.?\s?o\.?\b/gi, "OOO"],
  [/\bout\s+of\s+order\b/gi, "out of order"],
  [/\bo\.?\s?o\.?\s?s\.?\b/gi, "OOS"],
  [/\bl\.?\s?o\.?\s?s\.?\b/gi, "LOS"],
  [/\bd\.?\s?b\.?\s?a\.?\b/gi, "DBA"],
  [/\bs\.?\s?d\.?\s?l\.?\s?y\.?\b/gi, "SDLY"],
  [/\bm\.?\s?p\.?\s?i\.?\b/gi, "MPI"],
  [/\ba\.?\s?r\.?\s?i\.?\b/gi, "ARI"],
  [/\br\.?\s?g\.?\s?i\.?\b/gi, "RGI"],
  [/\bb\.?\s?a\.?\s?r\.?\s+rate\b/gi, "BAR rate"],
  [/\bcomp\s*[- ]?\s*set\b/gi, "comp set"],
  [/\bcompset\b/gi, "comp set"],
  [/\bpick\s*[- ]?\s*up\b/gi, "pickup"],
  [/\bwalk\s*[- ]?\s*in(s)?\b/gi, "walk-in$1"],
  [/\bno\s*[- ]?\s*show(s)?\b/gi, "no-show$1"],
  [/\bgroup\s*block(s)?\b/gi, "group block$1"],
  // brand / product names
  [/\blodge\s*[- ]?\s*(iq|i\.?\s?q\.?)\b/gi, "LodgIQ"],
  [/\blodging\s*[- ]?\s*(iq|i\.?\s?q\.?)\b/gi, "LodgIQ"],
  [/\blodgiq\b/gi, "LodgIQ"],
  [/\bgroup\s*[- ]?\s*(iq|i\.?\s?q\.?)\b/gi, "GroupIQ"],
  [/\brate\s*[- ]?\s*push\b/gi, "rate push"],
];

const MONTHS = {
  january: "Jan", february: "Feb", march: "Mar", april: "Apr", may: "May",
  june: "Jun", july: "Jul", august: "Aug", september: "Sep", october: "Oct",
  november: "Nov", december: "Dec",
};

const ORDINALS = {
  first: 1, second: 2, third: 3, fourth: 4, fifth: 5, sixth: 6, seventh: 7,
  eighth: 8, ninth: 9, tenth: 10, eleventh: 11, twelfth: 12, thirteenth: 13,
  fourteenth: 14, fifteenth: 15, sixteenth: 16, seventeenth: 17, eighteenth: 18,
  nineteenth: 19, twentieth: 20, "twenty first": 21, "twenty second": 22,
  "twenty third": 23, "twenty fourth": 24, "twenty fifth": 25, "twenty sixth": 26,
  "twenty seventh": 27, "twenty eighth": 28, "twenty ninth": 29, thirtieth: 30,
  "thirty first": 31,
};

/**
 * Clean a dictated question: house vocabulary, spoken dates ("august first" ->
 * "Aug 1"), spoken currency ("449 dollars" -> "$449"), and stray spacing.
 * Pure and side-effect free — safe to unit test.
 */
export function fixTranscript(raw) {
  if (!raw) return "";
  let t = String(raw);

  // "<month> <ordinal|number>" -> "<Mon> <n>"  (also handles "1st", "21st")
  const monthNames = Object.keys(MONTHS).join("|");
  const ordinalWords = Object.keys(ORDINALS).sort((a, b) => b.length - a.length).join("|");
  t = t.replace(new RegExp(`\\b(${monthNames})\\s+(${ordinalWords})\\b`, "gi"),
    (_m, mon, ord) => `${MONTHS[mon.toLowerCase()]} ${ORDINALS[ord.toLowerCase()]}`);
  t = t.replace(new RegExp(`\\b(${monthNames})\\s+(\\d{1,2})(st|nd|rd|th)?\\b`, "gi"),
    (_m, mon, day) => `${MONTHS[mon.toLowerCase()]} ${parseInt(day, 10)}`);

  // spoken money -> symbol form; keep decimals if dictated
  t = t.replace(/\b(\d[\d,]*(?:\.\d+)?)\s*(?:us\s*)?dollars?\b/gi, (_m, n) => `$${n}`);
  // spoken percent
  t = t.replace(/\b(\d[\d.]*)\s*percent\b/gi, (_m, n) => `${n}%`);

  TERMS.forEach(([re, to]) => { t = t.replace(re, to); });

  // tidy: collapse spaces, drop space before punctuation, capitalise the opener
  t = t.replace(/\s{2,}/g, " ").replace(/\s+([,.?!])/g, "$1").trim();
  if (t) t = t.charAt(0).toUpperCase() + t.slice(1);
  return t;
}

export default fixTranscript;
